import React, { useCallback } from 'react';

import { di } from 'react-magnetic-di';
import { shallowEqualObjects } from 'shallow-equal';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import {
	useEmptyChatLaunchSelector,
	useUIStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { isForYouTabEnabled } from '@atlassian/rovo-for-you/for-you-tab-enabled';

import { useActiveTabContext } from '../active-tab-provider/useActiveTabContext';
import { ChatMainHeader as ChatMainHeaderRefresh } from '../chat-header-refresh';
import { FOR_YOU_TAB_ID } from '../chat-history/rovo-tabs/constants';

/**
 * Renders ChatMainHeaderRefresh with tab-aware onResetEmptyStateTab.
 * Must be rendered inside ActiveTabProvider.
 */
const ActiveTabAwareChatHeaderComponent = (
	props: React.ComponentProps<typeof ChatMainHeaderRefresh>,
): React.JSX.Element => {
	di(ChatMainHeaderRefresh, fg);
	const { resetToPreviousTab, handleActiveTabChange } = useActiveTabContext();
	const { setAIFeatureContext } = useChatContextStoreActions();
	const { clearSelectedSpace, clearEmptyChatLaunchAll } = useUIStoreActions();
	const [{ customGreeting: runtimeCustomGreeting }] = useEmptyChatLaunchSelector();
	const isEmptyChatLaunchActive = Boolean(runtimeCustomGreeting);

	const handleResetEmptyStateTab = useCallback(() => {
		if (isEmptyChatLaunchActive) {
			// When returning from the project chat empty state, navigate directly
			// to the For You tab (where the user originally clicked "Chat").
			handleActiveTabChange(FOR_YOU_TAB_ID);
		} else {
			resetToPreviousTab();
		}
		// Clear project context so the banner resets to default URL-based behavior
		setAIFeatureContext({
			contextKey: 'projectContext',
			setContext: <T,>(_: T): T => ({}) as T,
		});
		// Clear the SpaceBar and empty launch state so they don't persist after navigating back
		clearSelectedSpace();
		clearEmptyChatLaunchAll();
	}, [
		isEmptyChatLaunchActive,
		handleActiveTabChange,
		resetToPreviousTab,
		setAIFeatureContext,
		clearSelectedSpace,
		clearEmptyChatLaunchAll,
	]);

	return (
		<ChatMainHeaderRefresh
			{...props}
			onResetEmptyStateTab={
				// Shell-level gate: this handler performs the chat/context teardown when leaving
				// the For You tab (clears project context, selected space and empty-chat launch
				// state). It must be wired whenever the tab can render — otherwise a pins-only
				// user navigates away with stale chat context.
				isForYouTabEnabled() ? handleResetEmptyStateTab : undefined
			}
		/>
	);
};

// Experiment-only: absorb parent render waves when header props are stable.
const MemoizedActiveTabAwareChatHeader = React.memo(
	ActiveTabAwareChatHeaderComponent,
	shallowEqualObjects,
);

export const ActiveTabAwareChatHeader = (
	props: React.ComponentProps<typeof ActiveTabAwareChatHeaderComponent>,
): React.JSX.Element => {
	return FeatureGates.getExperimentValue(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	) ? (
		<MemoizedActiveTabAwareChatHeader {...props} />
	) : (
		<ActiveTabAwareChatHeaderComponent {...props} />
	);
};
